import { redirect } from "react-router";
import { getAvailableTenants } from "~/hooks/use-available-tenants";
import { authService } from "./auth-service";
import { getCurrentTenantId } from "./tenant-context";

export const LAST_TENANT_KEY = "atomic_last_tenant_id";

/**
 * Get the last active tenant ID from localStorage
 */
export const getLastTenantId = (): string | null => {
  try {
    return localStorage.getItem(LAST_TENANT_KEY);
  } catch {
    return null;
  }
};

/**
 * Set the last active tenant ID in localStorage
 * Should only be called when user explicitly switches tenants
 */
export const setLastTenantId = (tenantId: string): void => {
  try {
    localStorage.setItem(LAST_TENANT_KEY, tenantId);
  } catch {
    // Ignore errors (e.g., private browsing mode)
  }
};

/**
 * Get the fallback tenant ID based on priority:
 * 1. localStorage tenant (if user has access)
 * 2. Single default tenant (if user has exactly one)
 * 3. null (user must select from multiple tenants)
 */
export const getFallbackTenantId = (
  accessibleTenants: Array<{ id: string; roles: string[] }>,
): string | null => {
  if (accessibleTenants.length === 0) {
    return null;
  }

  // Try localStorage tenant first
  const lastTenantId = getLastTenantId();
  if (lastTenantId && accessibleTenants.some((t) => t.id === lastTenantId)) {
    return lastTenantId;
  }

  // Fall back to single tenant
  if (accessibleTenants.length === 1) {
    return accessibleTenants[0].id;
  }

  // Multiple tenants and no valid localStorage - user must choose
  return null;
};

/**
 * Middleware that validates tenant access for /t/:tenantId routes
 *
 * Behavior:
 * - /t (no tenantId): Allows access, user selects tenant from sidebar
 * - /t/{tenantId}/* (with valid tenantId): Allows access, updates localStorage
 * - /t/{tenantId}/* (with INVALID tenantId): Redirects to /t with error
 *
 * Important: Does NOT use fallback tenant when URL has invalid tenant
 * Fallback tenant logic (localStorage/single tenant) only applies to routes WITHOUT tenant in URL
 *
 * Note: Tenant context is set by tenant-context-middleware (runs before this)
 */
export const tenantMiddleware = async () => {
  const user = authService.user();

  // If no user, let auth middleware handle it
  if (!user?.teams) {
    return null;
  }

  // Get tenant ID from context (set by tenant-context-middleware)
  const tenantId = getCurrentTenantId();

  // If no tenantId, this is /t - allow access
  if (!tenantId) {
    return null;
  }

  // We have a tenantId - validate user has access to it
  const hasAccess = getAvailableTenants(user.teams).some(
    (team) => team.id === tenantId,
  );

  if (hasAccess) {
    // User has valid access - update localStorage with this tenant
    // This ensures the last accessed tenant is remembered for future sessions
    setLastTenantId(tenantId);
  }

  if (!hasAccess) {
    // User doesn't have access to this tenant - ALWAYS redirect to selector
    // Do NOT use fallback tenant when the URL explicitly has an invalid tenant
    throw redirect("/t?error=tenant_access_denied");
  }

  return null;
};
